/**
 * ForensicLogger - Safe, low-volume forensic logging for camera/mic device details
 *
 * Provides utilities for logging device information used in hardware access checks
 * and Fortknox recording streams, with PII protection and rate limiting.
 */

const MAX_STRING_LENGTH = 50;
const DEVICE_ID_PREFIX_LENGTH = 6;
const DEVICE_ID_SUFFIX_LENGTH = 6;

// Rate limiting storage - maps log keys to timestamps
const loggedKeys = new Map();

/**
 * Creates a simple hash of a string for PII protection
 * @param {string} str - String to hash
 * @returns {string} Hashed string (first 8 chars of hex hash)
 */
function hashString(str) {
    if (!str || typeof str !== 'string') {
        return 'null';
    }

    // Simple hash function for client-side use (not cryptographic)
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char; // eslint-disable-line no-bitwise
        hash &= hash; // eslint-disable-line no-bitwise
    }

    // Convert to hex and return first 8 characters
    return Math.abs(hash).toString(16).substring(0, 8);
}

/**
 * Redacts a device ID by showing only prefix and suffix
 * @param {string} deviceId - Full device ID
 * @returns {string} Redacted device ID (e.g., "abc123...xyz789")
 */
function redactDeviceId(deviceId) {
    if (!deviceId || typeof deviceId !== 'string') {
        return 'null';
    }

    if (deviceId.length <= DEVICE_ID_PREFIX_LENGTH + DEVICE_ID_SUFFIX_LENGTH) {
        // For short device IDs, show first half and mask rest
        const showLength = Math.ceil(deviceId.length / 2);
        return `${deviceId.substring(0, showLength)}...`;
    }

    const prefix = deviceId.substring(0, DEVICE_ID_PREFIX_LENGTH);
    const suffix = deviceId.substring(deviceId.length - DEVICE_ID_SUFFIX_LENGTH);
    return `${prefix}...${suffix}`;
}

/**
 * Truncates a string to a maximum length
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length (default: MAX_STRING_LENGTH)
 * @returns {string} Truncated string
 */
function truncateString(str, maxLength = MAX_STRING_LENGTH) {
    if (!str || typeof str !== 'string') {
        return String(str);
    }

    if (str.length <= maxLength) {
        return str;
    }

    return `${str.substring(0, maxLength)}...`;
}

/**
 * Extracts safe track information from a MediaStreamTrack
 * @param {MediaStreamTrack} track - Media stream track
 * @returns {object} Safe track info with redacted deviceId and hashed label
 */
function extractTrackInfo(track) {
    if (!track) {
        return { kind: 'none', labelHash: 'null', deviceId: 'null', settings: {} };
    }

    const settings = track.getSettings ? track.getSettings() : {};

    return {
        kind: track.kind || 'unknown',
        labelHash: hashString(track.label || 'unlabeled'),
        deviceId: redactDeviceId(settings.deviceId || track.deviceId),
        settings: {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            aspectRatio: settings.aspectRatio,
            facingMode: settings.facingMode,
            sampleRate: settings.sampleRate,
            sampleSize: settings.sampleSize,
            channelCount: settings.channelCount,
        },
    };
}

/**
 * Extracts track info from a MediaStream
 * @param {MediaStream} stream - Media stream
 * @returns {object} Video and audio track info
 */
function extractStreamInfo(stream) {
    if (!stream) {
        return { video: null, audio: null };
    }

    const videoTracks = stream.getVideoTracks ? stream.getVideoTracks() : [];
    const audioTracks = stream.getAudioTracks ? stream.getAudioTracks() : [];

    return {
        video: videoTracks.length > 0 ? extractTrackInfo(videoTracks[0]) : null,
        audio: audioTracks.length > 0 ? extractTrackInfo(audioTracks[0]) : null,
    };
}

/**
 * Checks if a log with the given key should be logged (rate limiting)
 * @param {string} key - Unique key for this log entry
 * @returns {boolean} True if should log, false if rate limited
 */
function shouldLog(key) {
    return !loggedKeys.has(key);
}

/**
 * Marks a log key as logged to prevent duplicate logs
 * @param {string} key - Unique key for this log entry
 */
function markAsLogged(key) {
    loggedKeys.set(key, Date.now());
}

/**
 * Logs device switch information (not rate-limited - logs every switch)
 * @param {string} webcamDeviceId - Selected webcam device ID
 * @param {string} audioDeviceId - Selected audio device ID
 * @param {MediaStream} stream - Granted local stream
 */
function logDeviceSwitch(webcamDeviceId, audioDeviceId, stream) {
    const streamInfo = extractStreamInfo(stream);

    const logData = {
        selected: {
            webcam: redactDeviceId(webcamDeviceId),
            audio: redactDeviceId(audioDeviceId),
        },
        grantedStream: streamInfo,
    };

    console.log(`[ForensicLog] Device switch: ${JSON.stringify(logData)}`);
}

/**
 * Logs recording stream creation (rate-limited to once per session)
 * @param {object} constraints - Requested device constraints
 * @param {MediaStream} stream - Recording local stream
 */
function logRecordingStreamCreation(constraints, stream) {
    const logKey = 'recordingStreamCreation';

    if (!shouldLog(logKey)) {
        return; // Rate limited
    }

    const streamInfo = extractStreamInfo(stream);

    // Extract device IDs from constraints if present
    const requestedWebcam = constraints?.video?.deviceId?.exact ||
                           constraints?.video?.deviceId ||
                           'default';
    const requestedAudio = constraints?.audio?.deviceId?.exact ||
                          constraints?.audio?.deviceId ||
                          'default';

    const logData = {
        requested: {
            webcam: redactDeviceId(requestedWebcam),
            audio: redactDeviceId(requestedAudio),
        },
        recordingStream: streamInfo,
    };

    console.log(`[ForensicLog] Recording stream created: ${JSON.stringify(logData)}`);

    markAsLogged(logKey);
}

/**
 * Logs which stream is being used for recording (rate-limited to once per session)
 * @param {boolean} isRecordingStream - True if RECORDING_LOCAL_STREAM is used
 * @param {MediaStream} stream - Stream being used
 */
function logRecorderStreamChoice(isRecordingStream, stream) {
    const logKey = 'recorderStreamChoice';

    if (!shouldLog(logKey)) {
        return; // Rate limited
    }

    const streamInfo = extractStreamInfo(stream);
    const streamType = isRecordingStream ? 'RECORDING_LOCAL_STREAM' : 'GRANTED_LOCAL_STREAM';

    const logData = {
        streamType,
        streamInfo,
    };

    console.log(`[ForensicLog] Recorder stream choice: ${JSON.stringify(logData)}`);

    markAsLogged(logKey);
}

/**
 * Logs video element track info used for websocket streaming
 * @param {MediaStream} stream - Stream from video element srcObject
 */
function logWebcamRecordStart(stream) {
    const logKey = 'webcamRecordStart';

    if (!shouldLog(logKey)) {
        return; // Rate limited
    }

    const streamInfo = extractStreamInfo(stream);

    const logData = {
        videoElementStream: streamInfo,
    };

    console.log(`[ForensicLog] Webcam record started: ${JSON.stringify(logData)}`);

    markAsLogged(logKey);
}

/**
 * Detects and warns about device ID mismatch (rate-limited to once per session)
 * @param {string} selectedWebcamId - Device ID from state/user selection
 * @param {MediaStream} actualStream - Actual stream being used
 * @param {string} selectedWebcamLabel - Optional label from state/user selection for fallback comparison
 */
function checkAndWarnDeviceMismatch(selectedWebcamId, actualStream, selectedWebcamLabel = null) {
    const logKey = 'deviceMismatch';

    if (!shouldLog(logKey)) {
        return; // Rate limited
    }

    if (!actualStream || !selectedWebcamId) {
        return; // Cannot compare
    }

    const videoTracks = actualStream.getVideoTracks ? actualStream.getVideoTracks() : [];
    if (videoTracks.length === 0) {
        return; // No video track to compare
    }

    const videoTrack = videoTracks[0];
    const trackSettings = videoTrack.getSettings ? videoTrack.getSettings() : {};
    const actualDeviceId = trackSettings.deviceId || videoTrack.deviceId;
    const trackLabel = videoTrack.label;

    // Check for mismatch
    let mismatchDetected = false;
    let mismatchReason = '';

    // Primary comparison: device IDs
    if (actualDeviceId && selectedWebcamId !== actualDeviceId) {
        mismatchDetected = true;
        mismatchReason = 'deviceId mismatch';
    // Fallback comparison: hashed labels when deviceId is unavailable
    } else if (!actualDeviceId && selectedWebcamLabel && trackLabel) {
        const selectedLabelHash = hashString(selectedWebcamLabel);
        const actualLabelHash = hashString(trackLabel);
        if (selectedLabelHash !== actualLabelHash) {
            mismatchDetected = true;
            mismatchReason = 'labelHash mismatch (deviceId unavailable)';
        }
    }

    if (mismatchDetected) {
        const logData = {
            reason: mismatchReason,
            selected: redactDeviceId(selectedWebcamId),
            selectedLabelHash: selectedWebcamLabel ? hashString(selectedWebcamLabel) : 'null',
            actual: {
                deviceId: redactDeviceId(actualDeviceId),
                labelHash: hashString(trackLabel),
            },
        };

        console.warn(`[ForensicLog] DEVICE MISMATCH DETECTED: ${JSON.stringify(logData)}`);

        markAsLogged(logKey);
    }
}

/**
 * Resets rate limiting for testing purposes
 */
function resetRateLimiting() {
    loggedKeys.clear();
}

export default {
    redactDeviceId,
    truncateString,
    hashString,
    extractTrackInfo,
    extractStreamInfo,
    logDeviceSwitch,
    logRecordingStreamCreation,
    logRecorderStreamChoice,
    logWebcamRecordStart,
    checkAndWarnDeviceMismatch,
    resetRateLimiting,
};
